import { useState, useRef } from 'react'
import { isValidPhoneNumber } from 'libphonenumber-js/min'
import { useTranslation } from 'react-i18next'
import { ChevronDown } from 'lucide-react'

// ~20 principaux pays (Europe + monde francophone + monde) — ordre alphabétique
// maxLocal : longueur max affichée (0 initial inclus si applicable)
const COUNTRIES = [
  { code: 'DE', dialCode: '+49', name: 'Allemagne', maxLocal: 11 },
  { code: 'DZ', dialCode: '+213', name: 'Algérie', maxLocal: 10 },
  { code: 'BE', dialCode: '+32', name: 'Belgique', maxLocal: 10 },
  { code: 'BR', dialCode: '+55', name: 'Brésil', maxLocal: 11 },
  { code: 'CA', dialCode: '+1', name: 'Canada', maxLocal: 10 },
  { code: 'ES', dialCode: '+34', name: 'Espagne', maxLocal: 10 },
  { code: 'US', dialCode: '+1', name: 'États-Unis', maxLocal: 10 },
  { code: 'FR', dialCode: '+33', name: 'France', maxLocal: 10 },
  { code: 'IE', dialCode: '+353', name: 'Irlande', maxLocal: 10 },
  { code: 'IT', dialCode: '+39', name: 'Italie', maxLocal: 10 },
  { code: 'LU', dialCode: '+352', name: 'Luxembourg', maxLocal: 10 },
  { code: 'MA', dialCode: '+212', name: 'Maroc', maxLocal: 10 },
  { code: 'MX', dialCode: '+52', name: 'Mexique', maxLocal: 10 },
  { code: 'MC', dialCode: '+377', name: 'Monaco', maxLocal: 8 },
  { code: 'NL', dialCode: '+31', name: 'Pays-Bas', maxLocal: 10 },
  { code: 'PT', dialCode: '+351', name: 'Portugal', maxLocal: 10 },
  { code: 'GB', dialCode: '+44', name: 'Royaume-Uni', maxLocal: 11 },
  { code: 'SN', dialCode: '+221', name: 'Sénégal', maxLocal: 9 },
  { code: 'CH', dialCode: '+41', name: 'Suisse', maxLocal: 10 },
  { code: 'TN', dialCode: '+216', name: 'Tunisie', maxLocal: 8 },
] as const

type CountryCode = (typeof COUNTRIES)[number]['code']

interface PhoneInputProps {
  value: string
  onChange: (fullNumber: string) => void
  required?: boolean
  fieldCls: string
  labelCls: string
  label: string
  placeholder?: string
}

export default function PhoneInput({
  onChange,
  required,
  fieldCls,
  labelCls,
  label,
  placeholder,
}: PhoneInputProps) {
  const { t } = useTranslation()
  const [countryCode, setCountryCode] = useState<CountryCode>('FR')
  const [localNumber, setLocalNumber] = useState('')
  const [error, setError] = useState('')
  const [open, setOpen] = useState(false)
  const dropdownRef = useRef<HTMLDivElement>(null)

  const selectedCountry = COUNTRIES.find((c) => c.code === countryCode)!

  // Strip leading 0 only when building the international number (not for display)
  const toInternational = (local: string) =>
    local ? `${selectedCountry.dialCode}${local.replace(/^0+/, '')}` : ''

  const handleLocalChange = (raw: string) => {
    // Keep only digits, cap at maxLocal (leading 0 counts toward the limit)
    const digits = raw.replace(/\D/g, '').slice(0, selectedCountry.maxLocal)
    setLocalNumber(digits)
    setError('')
    onChange(toInternational(digits))
  }

  const handleCountryChange = (code: CountryCode) => {
    setCountryCode(code)
    setLocalNumber('')
    setError('')
    onChange('')
    setOpen(false)
  }

  const handleBlur = () => {
    if (!localNumber) {
      if (required) setError(t('phoneInput.required', 'Numéro de téléphone requis'))
      return
    }
    const full = toInternational(localNumber)
    if (!isValidPhoneNumber(full, countryCode)) {
      setError(t('phoneInput.invalid', 'Numéro de téléphone invalide'))
    } else {
      setError('')
    }
  }

  return (
    <div>
      <label className={labelCls}>{label}</label>
      <div className="flex gap-1.5">
        {/* Country dropdown */}
        <div className="relative" ref={dropdownRef}>
          <button
            type="button"
            onClick={() => setOpen((o) => !o)}
            className={`${fieldCls} flex items-center gap-1 pr-1 pl-2 whitespace-nowrap w-auto`}
            aria-haspopup="listbox"
            aria-expanded={open}
          >
            <span className="text-xs text-gray-600">{selectedCountry.dialCode}</span>
            <ChevronDown
              className={`w-3 h-3 text-gray-400 transition-transform ${open ? 'rotate-180' : ''}`}
            />
          </button>

          {open && (
            <ul
              role="listbox"
              className="absolute z-50 top-full left-0 mt-1 w-52 bg-white border border-gray-200 rounded-lg shadow-lg max-h-60 overflow-y-auto py-1"
            >
              {COUNTRIES.map((c) => (
                <li
                  key={c.code}
                  role="option"
                  aria-selected={c.code === countryCode}
                  onClick={() => handleCountryChange(c.code as CountryCode)}
                  className={`flex items-center gap-2 px-3 py-1.5 cursor-pointer text-sm hover:bg-gray-50
                    ${c.code === countryCode ? 'bg-primary-50 text-primary-700 font-medium' : 'text-gray-700'}`}
                >
                  <span className="text-base">{c.code}</span>
                  <span className="flex-1 truncate">{c.name}</span>
                  <span className="text-xs text-gray-400">{c.dialCode}</span>
                </li>
              ))}
            </ul>
          )}
        </div>

        {/* Local number input */}
        <div className="flex-1">
          <input
            type="tel"
            value={localNumber}
            onChange={(e) => handleLocalChange(e.target.value)}
            onBlur={handleBlur}
            required={required}
            maxLength={selectedCountry.maxLocal}
            placeholder={placeholder}
            className={`${fieldCls} w-full ${error ? 'border-red-400 focus:ring-red-400' : ''}`}
            aria-invalid={!!error}
          />
        </div>
      </div>

      {error && <p className="mt-1 text-xs text-red-600">{error}</p>}
    </div>
  )
}
